import type { ComponentPropsWithRef, ReactNode } from 'react'
import { cx } from '@/lib/cx'
import styles from './Button.module.css'
import { Icon, type IconName } from './Icon'
import { VisuallyHidden } from './VisuallyHidden'

interface ButtonOwnProps {
  /**
   * - `primary` (default): iris fill, white text, a soft iris glow; hover deepens to iris-600.
   * - `accent`: prism glass, frosted with an iridescent hairline edge. Put it over colour
   *   (`<PrismLight />` or a scene) so the glass reads.
   * - `ghost`: frosted glass.
   * - `link`: text with a refined underline that wipes through on hover.
   */
  variant?: 'primary' | 'accent' | 'ghost' | 'link'
  /** Default `md` (44px tall, the minimum touch target). `lg` is for hero and CTA moments. */
  size?: 'md' | 'lg'
  /** Optional trailing icon. */
  icon?: IconName
  className?: string
  children: ReactNode
}

/** Renders a `<button type="button">`. */
export type ButtonAsButtonProps = ButtonOwnProps &
  Omit<ComponentPropsWithRef<'button'>, keyof ButtonOwnProps | 'type'> & {
    href?: undefined
    external?: undefined
  }

/** Renders an `<a>`. Set `external` for off-site links (new tab, `rel="noopener noreferrer"`). */
export type ButtonAsLinkProps = ButtonOwnProps &
  Omit<ComponentPropsWithRef<'a'>, keyof ButtonOwnProps | 'href'> & {
    href: string
    external?: boolean
  }

/** Props for {@link Button}: pass `href` to get a link, omit it to get a button. */
export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps

/**
 * The site's button and link-button. It declares `data-cursor="link"` for the custom
 * cursor. Wrap it in `<Magnetic>` for the magnetic hover.
 *
 * @example
 * <Button href={site.telegramUrl} external icon="arrow-up-right" size="lg">Talk to Vokarta</Button>
 * <Button variant="accent" href={cta.button.href} external>{cta.button.label}</Button>
 * <Button variant="ghost" onClick={shuffle} icon="shuffle">Shuffle</Button>
 */
export function Button(props: ButtonProps) {
  const { variant = 'primary', size = 'md', icon, className, children } = props
  const classes = cx(styles.button, styles[variant], variant !== 'link' && styles[size], className)
  const content = (
    <>
      <span className={styles.label}>{children}</span>
      {icon ? <Icon name={icon} className={styles.icon} data-icon={icon} /> : null}
    </>
  )

  if (props.href !== undefined) {
    const {
      variant: _variant,
      size: _size,
      icon: _icon,
      className: _className,
      children: _children,
      external,
      ...anchorProps
    } = props
    return (
      <a
        {...anchorProps}
        className={classes}
        data-cursor="link"
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
      >
        {content}
        {external ? <VisuallyHidden> (opens in a new tab)</VisuallyHidden> : null}
      </a>
    )
  }

  const {
    variant: _variant,
    size: _size,
    icon: _icon,
    className: _className,
    children: _children,
    href: _href,
    external: _external,
    ...buttonProps
  } = props
  return (
    <button type="button" {...buttonProps} className={classes} data-cursor="link">
      {content}
    </button>
  )
}
